/**
 * ResponseCache — localStorage-backed response cache for BatchOrchestrator.
 *
 * Eliminates redundant Bitrix24 API calls across page reloads by caching
 * method responses in localStorage with per-method TTLs.
 *
 * Design principles:
 *  - Versioned key schema (per `client-localstorage-schema` Vercel rule)
 *  - ~2 MB storage budget enforced by evicting oldest entries on overflow
 *  - Deterministic cache keys from method + JSON.stringify(params)
 *  - try/catch around all localStorage ops (incognito, quota, disabled)
 */

// ── Constants ─────────────────────────────────────────────────────────────────

const CACHE_VERSION = "v1";
const STORAGE_KEY = `cmn-api-cache:${CACHE_VERSION}`;

/** Approximate storage budget in bytes (~2 MB). */
const MAX_STORAGE_BYTES = 2_097_152;

/** Default TTL per method category (ms — 5 minutes). */
const DEFAULT_TTL_MS = 300_000;

/** Methods whose data changes very rarely get a longer TTL (30 minutes). */
const LONG_TTL_METHODS = new Set([
  "crm.status.list",
  "crm.category.list",
  "crm.enum.ownertype",
  "crm.currency.list",
  "crm.item.fields",
]);
const LONG_TTL_MS = 1_800_000;

// ── Types ─────────────────────────────────────────────────────────────────────

interface CacheEntry {
  /** Cached response data */
  data: unknown;
  /** Timestamp when entry was written (ms since epoch) */
  ts: number;
  /** TTL for this entry (ms) */
  ttl: number;
}

interface CacheStore {
  [key: string]: CacheEntry;
}

// ── ResponseCache ─────────────────────────────────────────────────────────────

export class ResponseCache {
  private store: CacheStore;

  constructor() {
    this.store = this.load();
  }

  /**
   * Build a deterministic cache key from method name + params.
   * Uses JSON.stringify for stable serialization (keys are sorted by JS spec
   * for simple objects; for nested objects the caller is responsible for
   * consistent param ordering — Bitrix24 REST params are flat).
   */
  static buildKey(method: string, params: object): string {
    return `${method}:${JSON.stringify(params)}`;
  }

  /**
   * Retrieve a cached response if present and not expired.
   * Returns `undefined` on cache miss or expiration.
   */
  get(key: string): unknown | undefined {
    const entry = this.store[key];
    if (!entry) return undefined;
    if (Date.now() - entry.ts > entry.ttl) {
      delete this.store[key];
      this.persist();
      return undefined;
    }
    return entry.data;
  }

  /**
   * Store a response in the cache. Enforces the storage budget by evicting
   * oldest entries when the serialized size approaches MAX_STORAGE_BYTES.
   */
  set(key: string, data: unknown, method: string): void {
    const ttl = LONG_TTL_METHODS.has(method) ? LONG_TTL_MS : DEFAULT_TTL_MS;
    this.store[key] = { data, ts: Date.now(), ttl };
    this.enforceQuota();
    this.persist();
  }

  /** Remove all expired entries. */
  prune(): void {
    const now = Date.now();
    let changed = false;
    for (const key of Object.keys(this.store)) {
      const entry = this.store[key]!;
      if (now - entry.ts > entry.ttl) {
        delete this.store[key];
        changed = true;
      }
    }
    if (changed) this.persist();
  }

  /** Clear the entire cache. */
  clear(): void {
    this.store = {};
    this.persist();
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private load(): CacheStore {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as CacheStore;
      // Prune expired entries on load
      const now = Date.now();
      for (const key of Object.keys(parsed)) {
        const entry = parsed[key]!;
        if (now - entry.ts > entry.ttl) {
          delete parsed[key];
        }
      }
      return parsed;
    } catch {
      return {};
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.store));
    } catch {
      // Quota exceeded or disabled — silently degrade
    }
  }

  /**
   * Evict oldest entries until serialized size is under the budget.
   * Entries are sorted by timestamp ascending (oldest first).
   */
  private enforceQuota(): void {
    let serialized: string;
    try {
      serialized = JSON.stringify(this.store);
    } catch {
      return;
    }

    if (serialized.length * 2 <= MAX_STORAGE_BYTES) return; // UTF-16 ≈ 2 bytes/char

    // Sort entries by timestamp, evict oldest first
    const entries = Object.entries(this.store).sort((a, b) => a[1].ts - b[1].ts);
    for (const [key] of entries) {
      delete this.store[key];
      try {
        serialized = JSON.stringify(this.store);
      } catch {
        break;
      }
      if (serialized.length * 2 <= MAX_STORAGE_BYTES) break;
    }
  }
}
